import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TablePagination,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import {
  CalendarToday as CalendarIcon,
  FilterList as FilterListIcon,
  InfoOutlined as InfoIcon,
} from "@mui/icons-material";
import {
  getTradeFilterOptions,
  searchHistoricalTrade,
} from "../../../services/Axios";
import HistoricalTradesTable from "../../table/HistoricalTradesTable";
import SearchField from "./SearchField";
import AverageButtons from "./AverageButtons";
import SecurityAveragesPanel from "./SecurityAveragesPanel";
import SpreadDialog from "./SpreadDialog";
import useTradeAverages from "./useTradeAverages";
import { prevBusinessDay, toISODate } from "./tradeAverages";

// Helper function to calculate date ranges
const getDateRange = (preset) => {
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth();
  const day = today.getDate();
  const dayOfWeek = today.getDay();

  let dateFrom, dateTo;

  switch (preset) {
    case "prev": {
      const prev = toISODate(prevBusinessDay(today));
      return { dateFrom: prev, dateTo: prev };
    }
    case "wtd":
      const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      dateFrom = new Date(year, month, day + mondayOffset);
      dateTo = today;
      break;
    case "mtd":
      dateFrom = new Date(year, month, 1);
      dateTo = today;
      break;
    case "qtd":
      const quarter = Math.floor(month / 3);
      dateFrom = new Date(year, quarter * 3, 1);
      dateTo = today;
      break;
    case "ytd":
      dateFrom = new Date(year, 0, 1);
      dateTo = today;
      break;
    case "last7":
      dateFrom = new Date(year, month, day - 7);
      dateTo = today;
      break;
    case "last30":
      dateFrom = new Date(year, month, day - 30);
      dateTo = today;
      break;
    case "1y":
      dateFrom = new Date(year - 1, month, day);
      dateTo = today;
      break;
    case "all":
      return { dateFrom: null, dateTo: null };
    default:
      return { dateFrom: null, dateTo: null };
  }

  return {
    dateFrom: dateFrom.toISOString().split("T")[0],
    dateTo: dateTo.toISOString().split("T")[0],
  };
};

const PAGE_SIZE = 50;

// Highlight for the selected date / quick filter chip: a quiet grey fill with
// a dark outline and bold text. (The theme's primary.main is dark slate with
// black contrast text, so a stock color="primary" chip is unreadable.)
const selectedChipSx = {
  backgroundColor: "#e2e8f0",
  color: "#0f172a",
  fontWeight: 700,
  border: "1px solid #0f172a",
  "&:hover, &:focus": { backgroundColor: "#cbd5e1" },
};

const TradeArchiveResults = memo(function TradeArchiveResults({
  loading,
  hasSearched,
  searchResults,
  totalCount,
  page,
  pageSize,
  onClearFilters,
  onPageChange,
  headerActions,
}) {
  const theme = useTheme();
  const paperBorder = {
    backgroundColor: theme.palette.background.paper,
    p: 4,
    borderRadius: 1,
    border: `1px solid ${theme.palette.divider}`,
    textAlign: "center",
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "300px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }
  if (!hasSearched) {
    return (
      <Box sx={paperBorder}>
        <CircularProgress />
      </Box>
    );
  }
  if (searchResults.length > 0) {
    const startRow = totalCount > 0 ? (page - 1) * pageSize + 1 : 0;
    const endRow = Math.min(page * pageSize, totalCount);
    const title =
      totalCount > 0
        ? `${startRow}–${endRow} of ${totalCount} trade${totalCount !== 1 ? "s" : ""}`
        : `${searchResults.length} trade${searchResults.length !== 1 ? "s" : ""} found`;

    return (
      <Box>
        <HistoricalTradesTable
          data={searchResults}
          title={title}
          headerActions={headerActions}
        />
        <TablePagination
          component="div"
          count={totalCount}
          page={page - 1}
          onPageChange={onPageChange}
          rowsPerPage={pageSize}
          rowsPerPageOptions={[pageSize]}
          labelDisplayedRows={({ from, to, count }) =>
            `${from}–${to} of ${count !== -1 ? count : `more than ${to}`}`
          }
        />
      </Box>
    );
  }
  return (
    <Box sx={paperBorder}>
      <Typography variant="h6" color="text.secondary">
        No trades found for the selected filters
      </Typography>
      <Typography variant="body2" color="text.secondary" mt={1}>
        Try adjusting your search query, date range, or filter selections.
      </Typography>
      <Button
        variant="outlined"
        size="small"
        onClick={onClearFilters}
        sx={{ mt: 2 }}
      >
        Clear All Filters
      </Button>
    </Box>
  );
});

export default function TradeArchive() {
  const theme = useTheme();
  const [searchResults, setSearchResults] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const isSearchingRef = useRef(false);
  const committedSearchQueryRef = useRef("");
  const [committedQuery, setCommittedQuery] = useState("");

  const [dateRange, setDateRange] = useState(() => getDateRange("last7"));
  const [isCustomDateRange, setIsCustomDateRange] = useState(false);
  // Which date preset chip is highlighted; null once dates are typed in.
  const [activeDatePreset, setActiveDatePreset] = useState("last7");

  const [filters, setFilters] = useState({
    strategyType: "",
    posAction: "",
    secType: "",
    brokerCode: "",
  });

  const [filterOptions, setFilterOptions] = useState({
    strategyTypes: [],
    posActions: [],
    secTypes: [],
    brokerCodes: [],
  });

  const dateRangePresets = useMemo(
    () => [
      { label: "Prev Day", value: "prev", tooltip: "Previous business day" },
      { label: "Last 7 Days", value: "last7" },
      { label: "WTD", value: "wtd", tooltip: "Week to Date" },
      { label: "MTD", value: "mtd", tooltip: "Month to Date" },
      { label: "QTD", value: "qtd", tooltip: "Quarter to Date" },
      { label: "YTD", value: "ytd", tooltip: "Year to Date" },
      { label: "Last 30 Days", value: "last30" },
      { label: "1Y", value: "1y", tooltip: "Last 1 Year" },
      { label: "All", value: "all", tooltip: "All Time" },
    ],
    [],
  );

  const filterPresets = useMemo(
    () => [
      { label: "Buy Only", filters: { posAction: "Buy" } },
      { label: "Sell Only", filters: { posAction: "Sell" } },
      { label: "Short Only", filters: { posAction: "Short" } },
      { label: "Cover Only", filters: { posAction: "Cover" } },
    ],
    [],
  );

  const handleSearch = useCallback(
    async (query = "", forceLoad = false, pageOverride = null) => {
      if (isSearchingRef.current) return;

      if (
        !forceLoad &&
        query &&
        query.trim().length < 3 &&
        !dateRange.dateFrom
      ) {
        return;
      }

      const pageToUse = pageOverride ?? page;
      if (pageOverride != null) {
        setPage(pageOverride);
      }

      isSearchingRef.current = true;
      setLoading(true);
      setError(null);
      setHasSearched(true);

      try {
        const { results, totalCount: count } = await searchHistoricalTrade(
          query?.trim() || "",
          {
            strategyType: filters.strategyType,
            posAction: filters.posAction,
            secType: filters.secType,
            brokerCode: filters.brokerCode,
            dateFrom: dateRange.dateFrom,
            dateTo: dateRange.dateTo,
          },
          { page: pageToUse, pageSize: PAGE_SIZE },
        );

        setSearchResults(results || []);
        setTotalCount(count ?? 0);
      } catch (err) {
        console.error("Search error:", err);
        setError(`Failed to search historical trades: ${err.message}`);
        setSearchResults([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
        isSearchingRef.current = false;
      }
    },
    [filters, dateRange, page],
  );

  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const options = await getTradeFilterOptions();
        setFilterOptions({
          strategyTypes:
            options.strategy_types?.map((item) => item.StrategyType) || [],
          posActions: options.pos_actions?.map((item) => item.PosAction) || [],
          secTypes: options.sec_types?.map((item) => item.SecType) || [],
          brokerCodes:
            options.broker_codes?.map((item) => item.BrokerCode) || [],
        });
      } catch (err) {
        console.error("Error fetching filter options:", err);
      }
    };

    fetchFilterOptions();
  }, []);

  // Defer initial search until after first paint so the UI appears immediately
  useEffect(() => {
    let timeoutId;
    const rafId = requestAnimationFrame(() => {
      timeoutId = setTimeout(() => {
        handleSearch("", true);
      }, 0);
    });
    return () => {
      cancelAnimationFrame(rafId);
      if (timeoutId != null) clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!hasSearched || isCustomDateRange || loading) return;

    setPage(1);
    handleSearch(committedSearchQueryRef.current, true, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.strategyType,
    filters.posAction,
    filters.secType,
    filters.brokerCode,
    dateRange.dateFrom,
    dateRange.dateTo,
  ]);

  const handleCommitSearch = useCallback(
    (query) => {
      committedSearchQueryRef.current = query;
      setCommittedQuery(query);
      setPage(1);
      handleSearch(query, true, 1);
    },
    [handleSearch],
  );

  const handlePageChange = useCallback(
    (event, newPage) => {
      const nextPage = newPage + 1;
      setPage(nextPage);
      handleSearch(committedSearchQueryRef.current, true, nextPage);
    },
    [handleSearch],
  );

  const handleFilterChange = useCallback((filterName, value) => {
    setFilters((prev) => ({ ...prev, [filterName]: value }));
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters({
      strategyType: "",
      posAction: "",
      secType: "",
      brokerCode: "",
    });
  }, []);

  const averageFilters = useMemo(
    () => ({
      ...filters,
      dateFrom: dateRange.dateFrom,
      dateTo: dateRange.dateTo,
    }),
    [filters, dateRange.dateFrom, dateRange.dateTo],
  );

  // Averages: per-security shows on the page, spread is a popup. Both share
  // one fetch and only work once a search or filter narrows the result.
  const averages = useTradeAverages(committedQuery, averageFilters);
  const { canAverage, ensureFresh, filterKey } = averages;
  // null = automatic (open whenever a search or filter is applied); true or
  // false once the user has chosen to show or hide the panel themselves.
  const [securityPref, setSecurityPref] = useState(null);
  const securityOpen = securityPref ?? canAverage;
  const securityVisible = securityOpen && (canAverage || securityPref === true);
  const [spreadOpen, setSpreadOpen] = useState(false);

  // Once the panel is open it stays open and follows the search and filters:
  // it recalculates (after a short pause, so a run of filter changes is one
  // request) whenever they change. One request per distinct filter set, so a
  // failed load is not retried in a loop.
  const autoLoadedKeyRef = useRef(null);
  const { load } = averages;
  useEffect(() => {
    if (!securityVisible || !canAverage) return undefined;
    if (autoLoadedKeyRef.current === filterKey) return undefined;
    const timer = setTimeout(() => {
      autoLoadedKeyRef.current = filterKey;
      load();
    }, 300);
    return () => clearTimeout(timer);
  }, [securityVisible, canAverage, filterKey, load]);

  const toggleSecurity = useCallback(() => {
    setSecurityPref(!securityVisible);
  }, [securityVisible]);

  const openSpread = useCallback(() => {
    setSpreadOpen(true);
    ensureFresh();
  }, [ensureFresh]);

  const averageButtons = useMemo(
    () => (
      <AverageButtons
        canAverage={canAverage}
        securityOpen={securityVisible}
        onToggleSecurity={toggleSecurity}
        onOpenSpread={openSpread}
      />
    ),
    [canAverage, securityVisible, toggleSecurity, openSpread],
  );

  const activeFilterCount = useMemo(() => {
    return Object.values(filters).filter((v) => v !== "").length;
  }, [filters]);

  const applyFilterPreset = useCallback((preset) => {
    setFilters((prev) => ({ ...prev, ...preset.filters }));
  }, []);

  const applyDateRangePreset = useCallback((presetValue) => {
    const range = getDateRange(presetValue);
    setDateRange(range);
    setActiveDatePreset(presetValue);
    setIsCustomDateRange(false);
  }, []);

  const handleDateChange = useCallback((field, value) => {
    setDateRange((prev) => ({ ...prev, [field]: value }));
    setActiveDatePreset(null);
    setIsCustomDateRange(true);
    setSearchResults([]);
    setHasSearched(false);
  }, []);

  return (
    <Box sx={{ p: { xs: 0.5, sm: 1 } }}>
      {/* Title with Instructions Toggle */}
      <Box sx={{ pt: 1.5, mb: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <Typography variant="h2" fontWeight="bold">
            Historical Trade Archive
          </Typography>
          <Button
            size="small"
            startIcon={<InfoIcon />}
            onClick={() => setShowInstructions(!showInstructions)}
            sx={{ textTransform: "none", minWidth: "auto" }}
          >
            {showInstructions ? "Hide" : "Show"} Tips
          </Button>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
          <CalendarIcon sx={{ fontSize: "1rem", color: "text.secondary" }} />
          <Typography variant="body2" color="text.secondary">
            {dateRange.dateFrom && dateRange.dateTo
              ? `${new Date(dateRange.dateFrom + "T00:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })} to ${new Date(dateRange.dateTo + "T00:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}`
              : "All Time"}
          </Typography>
        </Box>
      </Box>

      {/* Usage Instructions */}
      <Collapse in={showInstructions}>
        <Alert severity="info" sx={{ mb: 2 }} icon={<InfoIcon />}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            How to Use This Tool
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            <li>
              <strong>Date Presets:</strong> Click any preset chip (Last 7 Days,
              MTD, YTD, etc.) for quick searches with automatic loading.
            </li>
            <li>
              <strong>Custom Dates:</strong> Manually enter dates for specific
              ranges. Click "Search" to load data (auto-search disabled to
              prevent large data pulls).
            </li>
            <li>
              <strong>Filters:</strong> Use dropdowns or quick action chips (Buy
              Only, Sell Only, etc.) to narrow results. Filters auto-update for
              preset dates.
            </li>
            <li>
              <strong>Search:</strong> Type 3+ characters to search by symbol,
              strategy, ISIN, ticker, or broker, then press Enter or click
              Search. Nothing is searched while you type.
            </li>
            <li>
              <strong>Best Practice:</strong> Start with a narrow date range or
              specific filters to avoid loading excessive data.
            </li>
          </Box>
        </Alert>
      </Collapse>

      {/* UNIFIED: Search, Date Range, and Filters Section */}
      <Box
        sx={{
          mb: 1.5,
          backgroundColor: theme.palette.background.paper,
          p: { xs: 1, sm: 1.5 },
          borderRadius: 1,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        {/* Custom Date Range Warning */}
        {isCustomDateRange && !hasSearched && (
          <Alert severity="info" sx={{ mb: 1 }}>
            <strong>Custom date range selected:</strong> Click the Search button
            when ready. Automatic search is disabled to prevent large data
            pulls.
          </Alert>
        )}

        {/* Date Inputs and Search in same row */}
        <Grid container spacing={1} sx={{ mb: 1 }}>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="From Date"
              type="date"
              value={dateRange.dateFrom || ""}
              onChange={(e) => handleDateChange("dateFrom", e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="To Date"
              type="date"
              value={dateRange.dateTo || ""}
              onChange={(e) => handleDateChange("dateTo", e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <SearchField
              onCommit={handleCommitSearch}
              loading={loading}
              autoSearch={false}
            />
          </Grid>
        </Grid>

        {/* Filter Dropdowns - Always Visible */}
        <Box sx={{ mb: 1 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <FilterListIcon color="action" />
              <Typography variant="subtitle1" fontWeight="medium">
                Filters
              </Typography>
              {activeFilterCount > 0 && (
                <Chip
                  label={`${activeFilterCount} active`}
                  size="small"
                  color="primary"
                />
              )}
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              {activeFilterCount > 0 && (
                <Button
                  variant="text"
                  size="small"
                  onClick={handleClearFilters}
                >
                  Clear All
                </Button>
              )}
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "flex", alignItems: "center", mr: 0.5 }}
              >
                Date:
              </Typography>
              {dateRangePresets.map((preset) => (
                <Chip
                  key={preset.value}
                  label={preset.label}
                  size="small"
                  onClick={() => applyDateRangePreset(preset.value)}
                  sx={[
                    { cursor: "pointer" },
                    activeDatePreset === preset.value && selectedChipSx,
                  ]}
                  variant={
                    activeDatePreset === preset.value ? "filled" : "outlined"
                  }
                  aria-pressed={activeDatePreset === preset.value}
                  title={preset.tooltip}
                />
              ))}
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "flex", alignItems: "center", ml: 1, mr: 0.5 }}
              >
                Quick:
              </Typography>
              {filterPresets.map((preset) => (
                <Chip
                  key={preset.label}
                  label={preset.label}
                  size="small"
                  onClick={() => applyFilterPreset(preset)}
                  sx={[
                    { cursor: "pointer" },
                    filters.posAction === preset.filters.posAction &&
                      selectedChipSx,
                  ]}
                />
              ))}
            </Box>
          </Box>

          <Grid container spacing={1}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Strategy Type</InputLabel>
                <Select
                  value={filters.strategyType}
                  label="Strategy Type"
                  onChange={(e) =>
                    handleFilterChange("strategyType", e.target.value)
                  }
                >
                  <MenuItem value="">All</MenuItem>
                  {filterOptions.strategyTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Action</InputLabel>
                <Select
                  value={filters.posAction}
                  label="Action"
                  onChange={(e) =>
                    handleFilterChange("posAction", e.target.value)
                  }
                >
                  <MenuItem value="">All</MenuItem>
                  {filterOptions.posActions.map((action) => (
                    <MenuItem key={action} value={action}>
                      {action}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Security Type</InputLabel>
                <Select
                  value={filters.secType}
                  label="Security Type"
                  onChange={(e) =>
                    handleFilterChange("secType", e.target.value)
                  }
                >
                  <MenuItem value="">All</MenuItem>
                  {filterOptions.secTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Broker</InputLabel>
                <Select
                  value={filters.brokerCode}
                  label="Broker"
                  onChange={(e) =>
                    handleFilterChange("brokerCode", e.target.value)
                  }
                >
                  <MenuItem value="">All</MenuItem>
                  {filterOptions.brokerCodes.map((broker) => (
                    <MenuItem key={broker} value={broker}>
                      {broker}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Active filter chips */}
          {activeFilterCount > 0 && (
            <Box sx={{ mt: 1, display: "flex", gap: 0.5, flexWrap: "wrap" }}>
              {filters.strategyType && (
                <Chip
                  label={`Strategy: ${filters.strategyType}`}
                  onDelete={() => handleFilterChange("strategyType", "")}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              )}
              {filters.posAction && (
                <Chip
                  label={`Action: ${filters.posAction}`}
                  onDelete={() => handleFilterChange("posAction", "")}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              )}
              {filters.secType && (
                <Chip
                  label={`Type: ${filters.secType}`}
                  onDelete={() => handleFilterChange("secType", "")}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              )}
              {filters.brokerCode && (
                <Chip
                  label={`Broker: ${filters.brokerCode}`}
                  onDelete={() => handleFilterChange("brokerCode", "")}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              )}
            </Box>
          )}
        </Box>

        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
      </Box>

      {securityVisible && (
        <SecurityAveragesPanel
          averages={averages}
          query={committedQuery}
          filters={averageFilters}
          onClose={() => setSecurityPref(false)}
        />
      )}
      <SpreadDialog
        open={spreadOpen}
        onClose={() => setSpreadOpen(false)}
        averages={averages}
        query={committedQuery}
        filters={averageFilters}
      />

      {/* Results Section - memoized so it does not re-render when only search input changes */}
      <Box sx={{ backgroundColor: theme.palette.background.default }}>
        <TradeArchiveResults
          loading={loading}
          hasSearched={hasSearched}
          searchResults={searchResults}
          totalCount={totalCount}
          page={page}
          pageSize={PAGE_SIZE}
          onClearFilters={handleClearFilters}
          onPageChange={handlePageChange}
          headerActions={averageButtons}
        />
      </Box>
    </Box>
  );
}
